import React, { FC, useState } from 'react';
import { Grid, Button, Tooltip } from '@material-ui/core';
import { Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import AuthorizationService from '../../../../authorization/AuthorizationService';
import DefaultVendorImg from '../../../../images/profile-placeholder.jpg';
import { DeleteConfirmationModal } from '../../../../components/DeleteConfirmationModal';
import { deleteOtherVendor } from '../../../../api/vendors/otherVendors';
import { fetchOtherVendorsList } from '../../../../actions/vendors/otherVendors';
import { toast } from 'react-toastify';


const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString();
};

export const VendorListRow: FC<{
  isBRAdmin: boolean;
  vendor: {
    vendor_logo: null | string;
    id: number;
    name: null | string;
    description: string;
    vendor_url: null | string;
    created_at: string;
  };
  pageNumber: number;
  pageSize: number;
}> = ({ isBRAdmin, vendor, pageNumber, pageSize }) => {
  const {
    id,
    name,
    description,
    vendor_logo,
    vendor_url,
    created_at,
  } = vendor ?? {};
  const [nav, setNav] = useState('none');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useDispatch();

  const handleViewVendor = () => {
    setNav('view');
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteOtherVendor(id);
      setIsDeleteModalOpen(false);
      toast.success(`Vendor "${name}" has been deleted successfully`);
      
      // Refresh the vendors list via Redux action
      dispatch(fetchOtherVendorsList() as any);
    } catch (error) {
      console.error('Error deleting vendor:', error);
      toast.error('Failed to delete vendor. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  if (nav === 'view') {
    return (
      <Navigate to={`/vendors/other-vendors/view/${id}/`} state={{ 
        detail: vendor,
        isFromVendorTab: true,
        pageNumber: pageNumber,
        pageSize: pageSize,
        path: '/vendors/other-vendors'  
      }} />
    );
  }
  if (nav === 'edit') {
    return (
      <Navigate
        to={`/vendors/other-vendors/edit/${id}/`}
        state={{
          detail: vendor,
          navigateFrom: 'LIST',
          pageType: 'EDIT',
          isFromVendorTab: true,
          pageNumber: pageNumber,
          pageSize: pageSize,
          path: '/vendors/other-vendors' 
        }}
      />
    );
  }
  return (
    <Grid
      style={{ cursor: 'pointer' }}
      container
      spacing={0}
      className="tr"
    >
      <Grid
        className="td xs-left-align xs-primary"
        item
        xs={12}
        lg={!isBRAdmin ? 4 : 3}
        container
        direction="row"
        alignItems="center"
        onClick={() => handleViewVendor()}
      >
        <div className="table-member-details">
          <div 
            className="member-img"
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              if (vendor_url) {
                e.stopPropagation();
                window.open(vendor_url, '_blank', 'noopener,noreferrer');
              }
            }}
            style={vendor_url ? { cursor: 'pointer' } : undefined}
          >
            <img src={vendor_logo ? `/${vendor_logo}` : DefaultVendorImg} alt={name || ''} />
          </div>
          <div className="member-profile-details">
            {name}
          </div>
        </div>
      </Grid>
      <Grid
        className="td xs-left-align xs-primary"
        item
        xs={12}
        lg={!isBRAdmin ? 6 : 6}
        container
        direction="row"
        alignItems="center"
        onClick={() => handleViewVendor()}
      >
        {description}
      </Grid>
      <Grid
        className="td xs-left-align"
        item
        xs={12}
        lg={2}
        container
        direction="row"
        alignItems="center"
        onClick={() => handleViewVendor()}
      >
        {created_at ? formatDate(created_at) : '-'}
      </Grid>
      {isBRAdmin && (
        <Grid
          className="td action-col"
          item
          xs={12}
          lg={1}
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
        >
          {AuthorizationService.isAuthorizedHTML('editvendorbutton') && (
            <Tooltip title="Edit">
              <Button
                type="button"
                className="btn-circle btn-secondary"
                variant="outlined"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setNav('edit');
                }}
              >
                <i className="fas fa-pen" />
              </Button>
            </Tooltip>
          )}
          {AuthorizationService.isAuthorizedHTML('deletevendorbutton') && (
            <Tooltip title="Delete">
              <Button
                type="button"
                className="btn-circle btn-danger"
                variant="outlined"
                style={{ borderColor: '#dc3545', color: '#dc3545' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteClick();
                }}
              >
                <i className="fas fa-trash" />
              </Button>
            </Tooltip>
          )}
        </Grid>
      )}

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        title="Delete Vendor"
        itemName={name || undefined}
        itemType="vendor"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={isDeleting}
      />
    </Grid>
  );
};

export default VendorListRow;
